// Die Planeten des Heimatsystems — ihr Ort im Feld und ihre Oberfläche.
//
// **Wortgleicher Spiegel von `backend/farmining/world/planets.py`** (FG-50).
// Zwei Rechnungen, zwei Saaten, und der Unterschied ist Absicht:
//
// * **Der Ort** hängt an der Weltsaat, wie bei Fundstellen und Wächter.
// * **Die Oberfläche** hängt **nicht** daran — ein Spieler, der das Feld
//   wechselt, fände sonst seine Fördertürme auf Gestein, wo gestern eine Ader
//   lag. Die Kolonie gehört dem Spieler, nicht dem Feld (`COLONY-001`).
//
// Beide Folgen berühren keinen Chunk und verschieben keine Erzziehung.

import { TAU, jsRound } from '../core/math';
import { hash3, rngFrom } from '../core/rng';
import { balancing, planetIndexOf } from '../data/balancing';
import type { Planet } from '../data/types';
import { eraSeed } from './field';

export interface Point {
  x: number;
  y: number;
}

/**
 * Der Mittelpunkt eines Planeten im Feld. **Die Ziehung ist Vertrag.**
 *
 * Nur die Richtung wird gezogen; der Abstand steht in der Tabelle.
 */
export function planetPoint(index: number, seed: number, era: number, out: Point): Point {
  const welt = eraSeed(seed, era);
  const rng = rngFrom(hash3(hash3(index, 0, welt), balancing().colony.planetSalt, welt));
  const angle = rng() * TAU;                                            // 1
  const radius = balancing().planets[index].dist;
  out.x = jsRound(Math.cos(angle) * radius);
  out.y = jsRound(Math.sin(angle) * radius);
  return out;
}

/**
 * Die Orte der laufenden Weltsaat — **ein** Eintrag, nicht je Abfrage gezogen.
 *
 * Umlaufbahn und Zeichnung fragen je Bild; eine neue Zufallsfolge je Planet und
 * Bild wäre Müll im Takt (FG-14). Ein Wechsel von Saat, Ära oder Tabellen
 * verwirft den Speicher.
 */
const orte: Point[] = [];
let orteVon: { seed: number; era: number; tables: unknown } = { seed: NaN, era: NaN, tables: null };

/** Der Ort eines Planeten nach seiner ID. */
export function planetAt(id: string, seed: number, era: number, out: Point): Point {
  const index = planetIndexOf(id);
  if (orteVon.seed !== seed || orteVon.era !== era || orteVon.tables !== balancing()) {
    orte.length = 0;
    orteVon = { seed, era, tables: balancing() };
  }
  if (!orte[index]) orte[index] = planetPoint(index, seed, era, { x: 0, y: 0 });
  out.x = orte[index].x;
  out.y = orte[index].y;
  return out;
}

const scratch: Point = { x: 0, y: 0 };

/** Steht das Schiff in der Umlaufbahn? In einer anderen Ära steht kein Planet. */
export function inOrbit(x: number, y: number, planet: Planet, seed: number, era: number): boolean {
  if (era < balancing().colony.minEra) return false;
  planetAt(planet.id, seed, era, scratch);
  const reach = planet.radius + balancing().colony.planetReach;
  return (scratch.x - x) ** 2 + (scratch.y - y) ** 2 <= reach * reach;
}

/**
 * Der Planet, in dessen Umlaufbahn das Schiff gerade steht — oder `null`.
 *
 * Für Anzeige und Landeknopf; gebucht wird die Umlaufbahn online vom Server.
 */
export function orbitAt(x: number, y: number, seed: number, era: number): Planet | null {
  for (const planet of balancing().planets) {
    if (inOrbit(x, y, planet, seed, era)) return planet;
  }
  return null;
}

// ── Die Oberfläche ───────────────────────────────────────────────────────────

/** Ein Vorkommen auf einer Kachel: ein Rohstoff oder eine Wärmequelle. */
export interface Deposit {
  kind: string;
  /** Ergiebigkeit 1…3 — der Index in `richness` plus eins. */
  rich: number;
}

/** Die Landekachel — die Mitte des Rasters. Dort steht die Kapsel. */
export function center(): Point {
  const c = balancing().colony;
  return { x: Math.floor(c.gridW / 2), y: Math.floor(c.gridH / 2) };
}

function pickDeposit(planet: Planet, roll: number): string {
  let total = 0;
  for (const entry of planet.deposits) total += entry.weight;
  let target = roll * total;
  for (const entry of planet.deposits) {
    target -= entry.weight;
    if (target < 0) return entry.kind;
  }
  return planet.deposits[planet.deposits.length - 1].kind;
}

function pickRich(roll: number): number {
  const odds = balancing().colony.richOdds;
  for (let i = 0; i < odds.length; i++) if (roll < odds[i]) return i + 1;
  return odds.length + 1;
}

const surfaces = new Map<number, Array<Deposit | null>>();
/**
 * Zu welchen Tabellen der Zwischenspeicher gehört. Kommen sie vom Server neu
 * (`adoptBalancing`), wird er verworfen — `data/` darf `sim/` nicht kennen
 * (FG-04), also fragt der Speicher selbst nach.
 */
let surfacesOf: unknown = null;

/**
 * Alle Vorkommen eines Planeten, zeilenweise (`y * W + x`).
 *
 * **Die Reihenfolge der Ziehungen ist Vertrag** (FG-50): je Kachel eine Zahl
 * für „liegt hier etwas"; nur wenn ja, zwei weitere für Art und Ergiebigkeit.
 * Danach werden die Startvorkommen gesetzt, ohne Ziehung, und die Landekachel
 * wird geräumt. Zwischengespeichert, weil sie sich nie ändert.
 */
export function surface(index: number): Array<Deposit | null> {
  if (surfacesOf !== balancing()) {
    surfaces.clear();
    surfacesOf = balancing();
  }
  const known = surfaces.get(index);
  if (known) return known;
  const planet = balancing().planets[index];
  const c = balancing().colony;
  const rng = rngFrom(hash3(index, c.surfaceSalt, c.surfaceSeed));
  const tiles: Array<Deposit | null> = [];
  for (let y = 0; y < c.gridH; y++) {
    for (let x = 0; x < c.gridW; x++) {
      if (rng() >= planet.density) {                                   // 1
        tiles.push(null);
        continue;
      }
      const kind = pickDeposit(planet, rng());                          // 2
      tiles.push({ kind, rich: pickRich(rng()) });                      // 3
    }
  }
  const mitte = center();
  for (const start of planet.starters) {
    tiles[(mitte.y + start.dy) * c.gridW + mitte.x + start.dx] = { kind: start.kind, rich: 1 };
  }
  tiles[mitte.y * c.gridW + mitte.x] = null;
  surfaces.set(index, tiles);
  return tiles;
}

/** Das Vorkommen auf einer Kachel — `null` heißt blankes Gestein. */
export function depositAt(planetId: string, x: number, y: number): Deposit | null {
  const c = balancing().colony;
  if (x < 0 || x >= c.gridW || y < 0 || y >= c.gridH) return null;
  const index = planetIndexOf(planetId);
  if (index < 0) return null;
  return surface(index)[y * c.gridW + x];
}
